/**
 * PAGES CONFIG — Single Source of Truth
 *
 * This file defines every page and sub-tab in the system.
 * It drives three things simultaneously:
 *   1. Sidebar navigation (App.js reads this)
 *   2. Permissions panel checkboxes (PermissionsPanel reads this)
 *   3. Backend seeder reference
 *
 * TO ADD A NEW PAGE: add an entry here. Done.
 * It automatically appears in sidebar AND permissions panel.
 * Also add its permissionKey to DefaultRolePermissionsSeeder.php on backend.
 *
 * icon: key name into the NAV_ICONS map in App.js
 * permissionKey: null  = always visible (no permission check needed)
 * ownerOnly: true      = skip from permissions panel (owners only ever see it)
 */

export const NAV_SECTIONS = [
  {
    section: 'Overview',
    items: [
      { key:'dashboard',       label:'Dashboard',             icon:'dashboard',    path:'/dashboard',         permissionKey:'page:dashboard' },
    ],
  },
  {
    section: 'Operations',
    items: [
      { key:'calendar',        label:'Monthly Planner',       icon:'planner',      path:'/planning',          permissionKey:'page:calendar' },
      { key:'contentcalendar', label:'Content Briefs',      icon:'content',      path:'/content',           permissionKey:'page:contentcalendar' },
      { key:'creatives',       label:'Creatives',             icon:'creatives',    path:'/creatives',          permissionKey:'page:creatives' },
      { key:'videoworks',      label:'Video Works',        icon:'videoworks',   path:'/videoworks',         permissionKey:'page:videoworks' },
      { key:'printmedia',      label:'Print Media',           icon:'printmedia',   path:'/printmedia',         permissionKey:'page:printmedia' },
      { key:'postcopy',        label:'Post Copy',             icon:'postcopy',     path:'/postcopy',           permissionKey:'page:postcopy' },
      { key:'scheduling',      label:'Scheduling & Posting',  icon:'scheduling',   path:'/scheduling',         permissionKey:'page:scheduling' },
      { key:'published',       label:'Published Content',     icon:'published',    path:'/published-content',  permissionKey:'page:scheduling', sharedKey:true },
      { key:'monitoring',      label:'Monitoring',            icon:'monitoring',   path:'/monitoring',         permissionKey:'page:monitoring' },
      { key:'pipeline',        label:'WO Pipeline',           icon:'pipeline',     path:'/pipeline',           permissionKey:'page:pipeline' },
      { key:'assignments',     label:'Work Assignments',      icon:'assignments',  path:'/assignments',        permissionKey:'page:assignments' },
      { key:'workorders',      label:'Work Orders',           icon:'workorders',   path:'/work-orders',        permissionKey:'page:calendar', sharedKey:true },
      { key:'approvals',   label:'Approvals',        icon:'approvals',   path:'/approvals',        permissionKey:'page:approvals' },
      { key:'specialdays',     label:'Special Days',          icon:'planner',      path:'/special-days',      permissionKey:'page:specialdays' },
      { key:'chat',        label:'Chat',             icon:'chat',        path:'/chat',             permissionKey:'page:chat' },
    ],
  },
  {
    section: 'Team',
    items: [
      { key:'team',        label:'Team Availability', icon:'team',        path:'/team',        permissionKey:'page:team' },
      { key:'reallocation',label:'Dynamic Realloc',  icon:'realloc',     path:'/reallocation', permissionKey:'page:reallocation' },
      { key:'touchpoints', label:'Touchpoints',      icon:'touchpoints', path:'/touchpoints',  permissionKey:'page:touchpoints' },
    ],
  },
  {
    section: 'Finance & KPI',
    items: [
      { key:'costs',       label:'Cost Tracking', icon:'costs',       path:'/costs',       permissionKey:'page:costs' },
      { key:'kpi',         label:'KPI Tracker',   icon:'kpi',         path:'/kpi',         permissionKey:'page:kpi' },
      { key:'performance', label:'Performance',   icon:'performance', path:'/performance', permissionKey:'page:performance' },
    ],
  },
  {
    section: 'Config',
    items: [
      { key:'clients',        label:'Clients',        icon:'clients',    path:'/clients',        permissionKey:'page:clients' },
      { key:'masterdata',     label:'Master Data',    icon:'masterdata', path:'/masterdata',     permissionKey:'page:masterdata' },
      { key:'settings',       label:'Settings',       icon:'settings',   path:'/settings',       permissionKey:'page:settings' },
      { key:'access-control', label:'Access Control', icon:'access',     path:'/access-control', permissionKey:'page:access-control' },
      { key:'billing',        label:'Billing',        icon:'billing',    path:'/billing',        permissionKey:'page:billing' },
    ],
  },
];

/**
 * Master Data sub-tabs.
 * permissionKey: null = always visible to anyone who can access Master Data.
 * sensitive: true     = shown as restricted in the permissions panel.
 */
export const MASTER_DATA_TABS = [
  { id:'depts',            icon:'🏢', label:'Departments',      permissionKey:null },
  { id:'roles',            icon:'🎭', label:'Roles',            permissionKey:null },
  { id:'team',             icon:'👥', label:'Team',             permissionKey:null },
  { id:'clients',          icon:'👔', label:'Clients',          permissionKey:null },
  { id:'platforms',        icon:'📱', label:'Platforms',        permissionKey:null },
  { id:'services',         icon:'📦', label:'Services',         permissionKey:null },
  { id:'campaigntypes',    icon:'🎯', label:'Campaign Types',   permissionKey:null },
  { id:'phases',           icon:'🔄', label:'Phases',           permissionKey:null },
  { id:'tasktypes',        icon:'🗂️', label:'Task Types',      permissionKey:null },
  { id:'contenttypes',     icon:'🗂',  label:'Output Type',     permissionKey:null },
  { id:'outputformats',    icon:'📐', label:'Output Formats',   permissionKey:null },
  { id:'vendors',          icon:'🤝', label:'Vendors',          permissionKey:null },
  { id:'holidays',         icon:'📅', label:'Holidays',         permissionKey:null },
  { id:'sla',              icon:'⏱️', label:'Turnaround',      permissionKey:null },
  { id:'tags',             icon:'🏷️', label:'Tags & Labels',   permissionKey:null },
  { id:'notifications',    icon:'🔔', label:'Notifications',    permissionKey:null },
  { id:'templates',        icon:'📝', label:'Templates',        permissionKey:null },
  { id:'folderstructure',  icon:'📁', label:'Folder Structure', permissionKey:null },
  { id:'dashboardwidgets', icon:'📊', label:'Dashboard Widgets',permissionKey:null },
  { id:'rates',            icon:'💸', label:'Rates',            permissionKey:'tab:masterdata:rates',            sensitive:true },
  { id:'contracts',        icon:'📑', label:'Contracts',        permissionKey:'tab:masterdata:contracts',        sensitive:true },
  { id:'paymentterms',     icon:'💳', label:'Payment Terms',    permissionKey:'tab:masterdata:paymentterms',     sensitive:true },
  { id:'integrations',     icon:'🌐', label:'Integrations',     permissionKey:'tab:masterdata:integrations',     sensitive:true },
  { id:'brandguidelines',  icon:'🎨', label:'Brand Guidelines', permissionKey:'tab:masterdata:brandguidelines',  sensitive:false },
  { id:'approvalcontacts', icon:'✅', label:'Approval Contacts',permissionKey:'tab:masterdata:approvalcontacts', sensitive:false },
  { id:'competitors',      icon:'🔍', label:'Competitors',      permissionKey:'tab:masterdata:competitors',      sensitive:false },
  { id:'reporttemplates',  icon:'📋', label:'Report Templates', permissionKey:'tab:masterdata:reporttemplates',  sensitive:false },
  { id:'internalapprovers',icon:'🔐', label:'Int. Approvers',   permissionKey:'tab:masterdata:internalapprovers',sensitive:true },
];

/** All unique page permissions assignable to non-owner users. */


/**
 * Operations page sub-tabs.
 * Each page key matches NAV_SECTIONS item key.
 * permissionKey format: tab:<pageKey>:<tabId>
 * By default all tabs are visible (permissionKey: null means always visible).
 * Add a permissionKey to make a tab controllable.
 */
export const OPERATIONS_TABS = {
  contentcalendar: [
    { id:'scripts',  label:'Scripts & Briefs',   icon:'📝', permissionKey:'tab:contentcalendar:scripts' },
    { id:'internal', label:'Internal Approval',  icon:'🔒', permissionKey:'tab:contentcalendar:internal' },
    { id:'client',   label:'Client Approval',    icon:'👔', permissionKey:'tab:contentcalendar:client' },
    { id:'approved', label:'Approved',           icon:'✅', permissionKey:'tab:contentcalendar:approved' },
  ],
  creatives: [
    { id:'pending',  label:'In Progress',        icon:'🎨', permissionKey:'tab:creatives:pending' },
    { id:'internal', label:'Internal Review',    icon:'🔒', permissionKey:'tab:creatives:internal' },
    { id:'client',   label:'Client Review',      icon:'👔', permissionKey:'tab:creatives:client' },
    { id:'approved', label:'Approved',           icon:'✅', permissionKey:'tab:creatives:approved' },
  ],
  postcopy: [
    { id:'draft',    label:'Draft',              icon:'✍️', permissionKey:'tab:postcopy:draft' },
    { id:'internal', label:'Internal Approval',  icon:'🔒', permissionKey:'tab:postcopy:internal' },
    { id:'client',   label:'Client Approval',    icon:'👔', permissionKey:'tab:postcopy:client' },
    { id:'approved', label:'Approved',           icon:'✅', permissionKey:'tab:postcopy:approved' },
  ],
  videoworks: [
    { id:'production', label:'Production',        icon:'📹', permissionKey:'tab:videoworks:production' },
    { id:'internal',   label:'Internal Review',   icon:'🔒', permissionKey:'tab:videoworks:internal' },
    { id:'client',     label:'Client Review',     icon:'👔', permissionKey:'tab:videoworks:client' },
    { id:'approved',   label:'Approved',           icon:'✅', permissionKey:'tab:videoworks:approved' },
  ],
  printmedia: [
    { id:'design',     label:'Design',             icon:'🖨️', permissionKey:'tab:printmedia:design' },
    { id:'internal',   label:'Internal Review',    icon:'🔒', permissionKey:'tab:printmedia:internal' },
    { id:'client',     label:'Client Review',      icon:'👔', permissionKey:'tab:printmedia:client' },
    { id:'approved',   label:'Approved',           icon:'✅', permissionKey:'tab:printmedia:approved' },
  ],
  scheduling: [
    { id:'ready',     label:'Ready to Schedule', icon:'🚀', permissionKey:'tab:scheduling:ready' },
    { id:'scheduled', label:'Scheduled',         icon:'🗓️', permissionKey:'tab:scheduling:scheduled' },
    { id:'posted',    label:'Posted',            icon:'✅', permissionKey:'tab:scheduling:posted' },
  ],
  monitoring: [
    { id:'active',   label:'Active Monitoring',  icon:'📡', permissionKey:'tab:monitoring:active' },
    { id:'closed',   label:'Closed',             icon:'✅', permissionKey:'tab:monitoring:closed' },
  ],
  settings: [
    { id:'branding',      label:'Branding',          icon:'🎨', permissionKey:'tab:settings:branding' },
    { id:'general',       label:'General Settings',  icon:'⚙️', permissionKey:'tab:settings:general' },
    { id:'workweek',      label:'Work Week',         icon:'📅', permissionKey:'tab:settings:workweek' },
    { id:'security',      label:'Security',          icon:'🔐', permissionKey:'tab:settings:security' },
    { id:'communication', label:'Communication',     icon:'📧', permissionKey:'tab:settings:communication' },
  ],
};

export function getAssignablePagePermissions() {
  const seen = new Set();
  const result = [];
  for (const section of NAV_SECTIONS) {
    for (const item of section.items) {
      if (!item.permissionKey) continue;
      if (item.ownerOnly) continue;
      if (item.sharedKey) continue;
      if (seen.has(item.permissionKey)) continue;
      seen.add(item.permissionKey);
      result.push({ key:item.permissionKey, label:item.label, icon:item.icon, section:section.section });
    }
  }
  return result;
}

/** Master Data tab permissions for all sub-tabs. */
export function getAssignableTabPermissions() {
  return MASTER_DATA_TABS
    .map(t => ({ key:t.permissionKey || `tab:masterdata:${t.id}`, label:t.label, icon:t.icon, sensitive:t.sensitive }));
}

/** Operations sub-tab permissions grouped by page key. */
export function getAssignableOperationsTabPermissions() {
  const result = {};
  for (const [pageKey, tabs] of Object.entries(OPERATIONS_TABS)) {
    result[pageKey] = tabs
      .filter(t => t.permissionKey !== null)
      .map(t => ({ key:t.permissionKey, label:t.label, icon:t.icon }));
  }
  return result;
}

/** Map from NAV_SECTIONS item key to its display label. */
export function getPageLabelMap() {
  const map = {};
  for (const section of NAV_SECTIONS) {
    for (const item of section.items) {
      map[item.key] = item.label;
    }
  }
  return map;
}
